// Lane wear, gutters, drains, patches and stop lines.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { pick, rr } from '../lib/random.js';
import { M, P } from '../lib/factory.js';
import { instanced } from '../lib/instancing.js';
import { streets } from './landmarks.js';
import { grateTex, paintMat } from './street-furniture.js';

export function buildRoadDetail() {
  // ---------- road surface detail: lane wear, gutters, drains, patches, stop lines ----------
  {
    const segs = [];
    for (const c of streets)
      for (let k = 0; k < 3; k++) {
        const a = streets[k],
          b = streets[k + 1];
        segs.push({ c, m: (a + b) / 2, L: b - a - 16 });
      }
    const flat = new THREE.PlaneGeometry(1, 1).rotateX(-PI / 2),
      lines = [];
    for (const sg of segs) for (const along of [false, true]) lines.push({ ...sg, along });
    const place = (off, w, y) => (o, l) => {
      o.position.set(l.along ? l.m : l.c + off, y, l.along ? l.c + off : l.m);
      o.rotation.y = l.along ? PI / 2 : 0;
      o.scale.set(w, 1, l.L + 4);
    };
    const wear = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.13,
      depthWrite: false,
    });
    for (const off of [-1.05, -0.35, 0.35, 1.05])
      instanced(flat, wear, lines, place(off, 0.3, 0.006)).castShadow = false;
    const gut = M(0x5d5a55, { flatShading: false, roughness: 0.6 });
    for (const off of [-2.1, 2.1])
      instanced(flat, gut, lines, place(off, 0.3, 0.008)).castShadow = false;
    const drains = [];
    for (const l of lines)
      for (let t = -l.L / 2 + 2; t < l.L / 2; t += 7)
        for (const off of [-2.05, 2.05])
          drains.push(
            l.along ? { x: l.m + t, z: l.c + off, r: 0 } : { x: l.c + off, z: l.m + t, r: PI / 2 },
          );
    instanced(
      new THREE.BoxGeometry(0.6, 0.02, 0.25),
      P(0xffffff, { map: grateTex, roughness: 0.5, metalness: 0.7 }),
      drains,
      (o, d) => {
        o.position.set(d.x, 0.012, d.z);
        o.rotation.y = d.r;
      },
    ).castShadow = false;
    const patches = [];
    for (let k = 0; k < 26; k++) {
      const l = pick(lines),
        t = rr(-l.L / 2, l.L / 2);
      patches.push({
        x: l.along ? l.m + t : l.c + rr(-1, 1),
        z: l.along ? l.c + rr(-1, 1) : l.m + t,
        w: rr(0.8, 2.2),
        d: rr(0.6, 1.8),
        r: rr(-0.1, 0.1),
      });
    }
    instanced(flat, M(0x605d59, { flatShading: false, roughness: 0.85 }), patches, (o, p) => {
      o.position.set(p.x, 0.004, p.z);
      o.rotation.y = p.r;
      o.scale.set(p.w, 1, p.d);
    }).castShadow = false;
    const stops = [];
    for (const ix of streets)
      for (const iz of streets) {
        if (iz + 8.6 < 46) stops.push({ x: ix + 1.1, z: iz + 8.6, r: 0 });
        if (iz - 8.6 > -46) stops.push({ x: ix - 1.1, z: iz - 8.6, r: 0 });
        if (ix + 8.6 < 46) stops.push({ x: ix + 8.6, z: iz - 1.1, r: PI / 2 });
        if (ix - 8.6 > -46) stops.push({ x: ix - 8.6, z: iz + 1.1, r: PI / 2 });
      }
    instanced(new THREE.BoxGeometry(2.1, 0.03, 0.32), paintMat, stops, (o, st) => {
      o.position.set(st.x, 0.015, st.z);
      o.rotation.y = st.r;
    }).castShadow = false;
  }
}
